import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import useDatasetSchemas from "datasets/hooks/useDatasetSchemas";
import useDatasets from "datasets/hooks/useDatasets";
import { promoteDatasetTable } from "datasets/pages/tables/api/promoteDatasetTable";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Dataset from "datasets/types/interfaces/dataset";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import Combobox from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import useToast from "~/components/ui/toast";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { nextGenDatasetDetailPath } from "~/router/constants";

interface PromoteTableModalProps {
  // The table to promote. `null` closes the dialog, so callers keep one piece
  // of state instead of a target and an `open` flag that can disagree.
  table: DatasetTableResponse | null;
  onClose: () => void;
  onPromoted: () => void;
}

interface PromoteForm {
  datasetId: string;
  schemaName: string;
  tableName: string;
  overwriteIfExists: boolean;
  dropAfterPromote: boolean;
}

interface PromoteTableFormProps {
  table: DatasetTableResponse;
  ownedDatasets: Dataset[];
  onClose: () => void;
  onPromoted: () => void;
}

// Keyed by the table's FQN from the parent, so switching to a different table
// remounts this with fresh defaults instead of reaching for an effect to sync them.
const PromoteTableForm = ({ table, ownedDatasets, onClose, onPromoted }: PromoteTableFormProps) => {
  const [error, setError] = useState("");
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const form = useForm<PromoteForm>({
    defaultValues: {
      datasetId: "",
      schemaName: "",
      tableName: table.name,
      overwriteIfExists: false,
      dropAfterPromote: false,
    },
    mode: "onChange",
  });
  const datasetId = form.watch("datasetId");
  const { data: schemasResp } = useDatasetSchemas({}, datasetId || undefined);
  const schemas = schemasResp?.items ?? [];
  const schemaName = form.watch("schemaName");

  // A dataset offering exactly one destination schema has nothing to choose:
  // select it automatically and disable the control below.
  const singleSchemaName = schemas.length === 1 ? schemas[0].name : null;

  useEffect(() => {
    if (singleSchemaName && schemaName !== singleSchemaName) {
      form.setValue("schemaName", singleSchemaName, { shouldValidate: true });
    }
  }, [singleSchemaName, schemaName, form]);

  const handleClose = () => {
    if (!form.formState.isSubmitting) {
      onClose();
    }
  };

  const onSubmit = async (data: PromoteForm) => {
    setError("");
    try {
      await promoteDatasetTable({
        source: { table_name: table.name, drop_after_promote: data.dropAfterPromote },
        destination: {
          dataset_id: data.datasetId,
          table_name: data.tableName,
          schema_name: data.schemaName,
          overwrite_if_exists: data.overwriteIfExists,
        },
      });
      onPromoted();
      const destinationDataset = ownedDatasets.find((d) => d.id === data.datasetId);
      showSuccess(
        `Promoted "${table.name}" to ${destinationDataset?.title ?? "the dataset"}`,
        undefined,
        // A Button with onClick(navigate), not react-router's Link: ShadcnToastProvider
        // renders the toast as a sibling of RouterProvider in App.tsx, outside the Router
        // context Link needs — but `navigate` was already captured above, during this
        // component's own render (which does have Router context), so calling it later
        // from here works regardless of where the button itself ends up rendering.
        <Button
          variant="link"
          size="sm"
          className="h-auto shrink-0 p-0 text-xs font-medium text-green-700 underline-offset-2 hover:text-green-800"
          onClick={() => navigate(`${nextGenDatasetDetailPath(data.datasetId)}/tables`)}
        >
          View tables
        </Button>
      );
      onClose();
    } catch (e: any) {
      const message = e.message ?? "Unable to promote table";
      setError(message);
      showError(`Unable to promote "${table.name}"`);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full min-w-0 flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Move this table out of your personal dataset into a dataset you own.
      </p>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="promote-dataset">Destination dataset</Label>
        <Controller
          control={form.control}
          name="datasetId"
          rules={{ required: true }}
          render={({ field }) => (
            <Combobox
              id="promote-dataset"
              value={field.value || null}
              onChange={(value) => {
                field.onChange(value);
                form.setValue("schemaName", "");
              }}
              placeholder="Select a dataset..."
              searchPlaceholder="Search datasets..."
              disabled={form.formState.isSubmitting}
              options={ownedDatasets.map((dataset) => ({
                value: dataset.id,
                label: dataset.title,
                icon: <DatasetIcon dataset={dataset} size={16} />,
              }))}
            />
          )}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="promote-schema">Destination schema</Label>
        <Controller
          control={form.control}
          name="schemaName"
          rules={{ required: true }}
          render={({ field }) => {
            const displaySchemaName = field.value || singleSchemaName || "";
            return (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={!datasetId || form.formState.isSubmitting || Boolean(singleSchemaName)}
              >
                <SelectTrigger id="promote-schema" className="w-full">
                  <SelectValue placeholder="Select a schema...">
                    {displaySchemaName && (
                      <span className="block min-w-0 truncate">{displaySchemaName}</span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {schemas.map((schema) => (
                    <SelectItem key={schema.name} value={schema.name}>
                      <span className="block min-w-0 truncate">{schema.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="promote-table-name">Table name</Label>
        <Controller
          control={form.control}
          name="tableName"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              id="promote-table-name"
              disabled={form.formState.isSubmitting}
              autoComplete="off"
            />
          )}
        />
      </div>

      <div className="flex items-center gap-2">
        <Controller
          control={form.control}
          name="overwriteIfExists"
          render={({ field }) => (
            <Checkbox
              id="promote-overwrite"
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={form.formState.isSubmitting}
            />
          )}
        />
        <Label htmlFor="promote-overwrite" className="font-normal">
          Overwrite if a table with this name already exists
        </Label>
      </div>

      <div className="flex items-center gap-2">
        <Controller
          control={form.control}
          name="dropAfterPromote"
          render={({ field }) => (
            <Checkbox
              id="promote-drop"
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={form.formState.isSubmitting}
            />
          )}
        />
        <Label htmlFor="promote-drop" className="font-normal">
          Remove from personal dataset after promoting
        </Label>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={form.formState.isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!form.formState.isValid}
          loading={form.formState.isSubmitting}
        >
          Promote
        </Button>
      </DialogFooter>
    </form>
  );
};

export const PromoteTableModal = ({ table, onClose, onPromoted }: PromoteTableModalProps) => {
  const { data: datasets } = useDatasets();
  const ownedDatasets = (datasets ?? [])
    .filter((d) => d.access_level === DatasetAccessLevel.OWNER)
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <Dialog open={table !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Promote &quot;{table?.name}&quot;</DialogTitle>
        </DialogHeader>
        {table && (
          <PromoteTableForm
            key={table.fullyQualifiedName}
            table={table}
            ownedDatasets={ownedDatasets}
            onClose={onClose}
            onPromoted={onPromoted}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
